/**
 * Ends intents whose operation is one or more debounced server writes with the outcome of
 * their LAST write, once nothing more can follow: the user is done (a slider
 * drag released), no change waits in the debounce and every write sent has answered. A
 * threshold drag can write during a pause and again after; an earlier answer, or one that
 * arrives after a newer write left, never decides it. No write at all ends it `ok` (a
 * client-only change).
 */
export function createWriteIntents() {
  /** intent id → { intent, holding, queued, inflight: Set<gen>, gen, outcome } */
  const live = new Map();

  const stateOf = (intent) => {
    if (!intent?.id || typeof intent.end !== "function") return null;
    let st = live.get(intent.id);
    if (!st) {
      st = { intent, holding: false, queued: false, inflight: new Set(), gen: 0, outcome: null };
      live.set(intent.id, st);
    }
    return st;
  };

  const tryEnd = (st) => {
    if (st.holding || st.queued || st.inflight.size > 0) return;
    live.delete(st.intent.id);
    st.intent.end(st.outcome ?? "ok");
  };

  return {
    /**
     * A change under `intent`: `holding` while more of the same gesture can follow (until
     * `release`), `queued` when it waits for a server write.
     */
    change(intent, { holding = false, queued = false } = {}) {
      const st = stateOf(intent);
      if (!st) return;
      st.holding = holding;
      if (queued) st.queued = true;
      tryEnd(st);
    },
    /** The gesture is over: nothing more follows `intent` but the writes already asked for. */
    release(intent) {
      const st = stateOf(intent);
      if (!st) return;
      st.holding = false;
      tryEnd(st);
    },
    /** The queued writes of `intents` leave as one request; returns its ticket for `settle`. */
    send(intents) {
      const ticket = [];
      for (const intent of intents) {
        const st = live.get(intent?.id);
        if (!st) continue;
        st.queued = false;
        st.gen += 1;
        st.inflight.add(st.gen);
        ticket.push([st, st.gen]);
      }
      return ticket;
    },
    /** The request behind `ticket` answered `outcome` (`ok` / `error`). */
    settle(ticket, outcome) {
      for (const [st, gen] of ticket) {
        st.inflight.delete(gen);
        if (gen === st.gen) st.outcome = outcome;
        if (live.get(st.intent.id) === st) tryEnd(st);
      }
    },
  };
}
